import { useId } from "react";
import type { JSX } from "react";
import { useI18n } from "../../shared/i18n/I18nContext";
import { cx } from "../../shared/ui/classNames";
import { useFocusOnMount } from "../../shared/ui/focus";
import styles from "../../shared/ui/ui.module.css";

/** Properties of {@link EarlyStartConfirmation}. */
export interface EarlyStartConfirmationProps {
  /** Formatted local time at which the rest period ends. */
  readonly restEnd: string;
  /** Records the clock-in despite the running rest period. */
  readonly onConfirm: () => void;
  /** Closes the confirmation without recording anything. */
  readonly onCancel: () => void;
}

/**
 * Confirmation before a clock-in during the daily rest period. It appears directly below the
 * clock action, receives the focus when it opens, and Escape cancels it (UX-004, UX-110).
 *
 * @param props - Component properties.
 * @returns The confirmation.
 */
export function EarlyStartConfirmation(props: EarlyStartConfirmationProps): JSX.Element {
  const { t } = useI18n();
  const panelRef = useFocusOnMount<HTMLDivElement>();
  const textId = useId();

  const confirmation = (
    <div
      ref={panelRef}
      role="group"
      aria-labelledby={textId}
      tabIndex={-1}
      className={cx(styles["alert"], styles["alertWarning"], styles["confirmAction"])}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          // Escape closes only the confirmation, not a panel around it (F-75).
          event.stopPropagation();
          props.onCancel();
        }
      }}
    >
      <p id={textId}>{t("clock.confirmEarlyStart", { time: props.restEnd })}</p>
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onCancel}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={props.onConfirm}
        >
          {t("clock.confirmEarlyStartAction")}
        </button>
      </div>
    </div>
  );

  return confirmation;
}
